import {QueryClient} from '@tanstack/react-query';
import {DriveFile} from './types';
import {queryKeys} from './queryKeys';
import {writeJsonFile} from './jsonFileStore';
import {JsonFileContent} from '../domain/json';

/** Finds a file or folder in any cached folder listing — lets a detail lookup skip its request. */
export function findFileInListings(queryClient: QueryClient, fileId: string): DriveFile | undefined {
    for (const [, files] of queryClient.getQueriesData<DriveFile[]>({queryKey: queryKeys.allFiles()})) {
        const match = files?.find((f) => f.id === fileId);
        if (match) return match;
    }
    return undefined;
}

/**
 * Merges a file's updated metadata (e.g. a write response) into every cached folder listing and
 * info.json index that contains it. Listings without it are left alone, keeping their age.
 */
export function patchFileInListings(queryClient: QueryClient, file: DriveFile): void {
    const patch = (files: DriveFile[] | undefined) => files?.some((f) => f.id === file.id)
        ? files.map((f) => f.id === file.id ? {...f, ...file} : f)
        : undefined;
    queryClient.setQueriesData<DriveFile[]>({queryKey: queryKeys.allFiles()}, patch);
    queryClient.setQueriesData<DriveFile[]>({queryKey: queryKeys.allInfoIndexes()}, patch);
}

/**
 * Caches the content of a JSON file we just wrote, so nothing has to download it again: under its
 * new version (also persisted), and in every entry already cached for the file — older versions
 * too, so a listing or search that still reports an older version can't bring back old content.
 */
export function seedJsonFile(queryClient: QueryClient, file: DriveFile, content: JsonFileContent): void {
    if (file.version) {
        queryClient.setQueryData(queryKeys.jsonFile(file.id, file.version), content);
        writeJsonFile(file.id, file.version, content);
    }
    queryClient.setQueriesData<JsonFileContent>({queryKey: queryKeys.jsonFileVersions(file.id)}, (old) =>
        old === undefined ? undefined : content);
}
